// Mirror of Application\Import\ImportQrCodeValidator in the monolith — keep both in sync.
// The range and TLD checks live in code rather than in the patterns: folding them in pushed
// both regexes past Sonar's complexity ceiling, and a Set of TLDs is easier to extend.
const QR_CODE_DATE_SHAPE = /^(\d{1,4})[-/.](\d{1,2})[-/.]\d{1,4}$/;
const QR_CODE_CLOCK_SHAPE = /^\d{1,2}:\d{2}(:\d{2})?(\.\d+)?$/;
const QR_CODE_TIME_ZONE_SHAPE = /(Z|[AP]M|[+-]\d{2}:?\d{2})$/i;
const QR_CODE_HOST_SHAPE = /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.([a-z]{2,})(?:[/?:#]|$)/i;
const QR_CODE_URL_TLDS = new Set([
  'com',
  'net',
  'org',
  'io',
  'me',
  'ly',
  'co',
  'es',
  'it',
  'fr',
  'de',
  'pt',
  'uk',
  'eu',
  'info',
  'link',
  'page',
  'app',
  'xyz',
  'gl',
  'to',
  'be',
  'nl',
  'cat',
]);
const MAX_MONTH_OR_DAY = 31;

const isMonthOrDay = (group: string) => {
  const value = Number.parseInt(group, 10);
  return value >= 1 && value <= MAX_MONTH_OR_DAY;
};

// The zone or meridiem suffix is stripped first so the clock pattern stays simple enough to
// keep both under Sonar's regex-complexity ceiling.
const isClockLike = (time: string) => QR_CODE_CLOCK_SHAPE.test(time.replace(QR_CODE_TIME_ZONE_SHAPE, '').trim());

export const qrCodeUrlSchemeValidator = /^([a-z][a-z0-9+.-]*:)?\/\//i;

// A 4-digit leading group is a year; a shorter one has to be a day or a month. Bounding both
// it and the middle group is what keeps grouped barcodes such as 1234-56-7890 out.
export const isQrCodeDateLike = (value: string) => {
  // \s so a tab- or newline-separated value from a mis-parsed TSV splits the same way, and
  // case-insensitive because lowercase t is valid RFC 3339. Mirrors preg_split's NO_EMPTY.
  // A value of only separators splits to nothing, so datePart has to fall back to a string
  // rather than relying on exec() coercing undefined.
  const [datePart, ...timeParts] = value.split(/[\sT]/i).filter((part) => part !== '');
  const groups = QR_CODE_DATE_SHAPE.exec(datePart ?? '');
  if (groups === null) return false;

  const [, leading, middle] = groups;
  if (!isMonthOrDay(middle)) return false;
  if (leading.length !== 4 && !isMonthOrDay(leading)) return false;

  const time = timeParts.join(' ');
  return time === '' || isClockLike(time);
};

// A known TLD rather than "any letters", so all-letter operator codes like IT.SIAE.ROMA and
// ticket.code are not read as hosts.
export const isQrCodeHostLike = (value: string) => {
  const groups = QR_CODE_HOST_SHAPE.exec(value);
  return groups !== null && QR_CODE_URL_TLDS.has(groups[1].toLowerCase());
};
